import { useEffect } from 'react';
import { Box, Button, Container, Divider, Stack, Typography } from '@mui/material';
import AlertTriangleIcon from '@assets/icons/alert-triangle.svg';
import RefreshIcon from '@mui/icons-material/Refresh';
import HomeIcon from '@mui/icons-material/Home';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import { makeStyles } from 'tss-react/mui';
import { getLogger } from '@logging/logger';

interface UnknownErrorProps {
  error?: unknown;
  reset?: () => void;
  info?: { componentStack?: string };
  title?: string;
  message?: string;
  subMessage?: string;
}

const useStyles = makeStyles()((theme) => ({
  container: {
    padding: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    gap: 2,
  },
  errorIcon: {
    width: 44,
    height: 44,
  },
  errorIconContainer: {
    borderRadius: '50%',
    width: 96,
    height: 96,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgb(254, 226, 226)',
  },
  reloadIcon: { width: 16, height: 16, marginRight: 8 },
}));
export function UnknownError({ error, info, title, message, subMessage }: UnknownErrorProps) {
  const { classes } = useStyles();
  useEffect(() => {
    getLogger().error('UnknownError page rendered', { route: window.location.pathname, componentStack: info?.componentStack }, error);
  }, [error, info]);
  return (
    <Container maxWidth='xl' className={classes.container}>
      <Stack
        spacing={4}
        direction={'column'}
        sx={{
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <div className={classes.errorIconContainer}>
          <img src={AlertTriangleIcon} alt='Error' className={classes.errorIcon} />
        </div>
        <Typography variant='h1'>{title || 'Something went wrong'}</Typography>
        <Typography variant='body1'>
          {message || 'We hit an unexpected error while loading your store. Our team has been notified and is looking into it.'}
        </Typography>
        <Typography variant='body1'>{subMessage || 'Your items and designs are safe — this is just a display issue.'}</Typography>
        <Box>
          <Stack direction={'row'} spacing={2}>
            <Button variant='contained' color='error' onClick={() => window.location.reload()}>
              <RefreshIcon /> &nbsp; Reload Page
            </Button>
            <Button variant='contained' size='large' color='primary' onClick={() => window.location.assign('https://www.squadlocker.com')}>
              <HomeIcon />
              &nbsp; SquadLocker Homepage
            </Button>
          </Stack>
        </Box>
        <Divider flexItem />
        <Typography variant='subtitle1'>Still having trouble? We're here to help.</Typography>

        <Button variant='contained' color='secondary' onClick={() => window.location.assign('https://www.squadlocker.com/email-us')}>
          <ContactSupportIcon />
          &nbsp; Contact Support
        </Button>
      </Stack>
    </Container>
  );
}
